import { useEffect, useState } from 'react';
import {
  getRtcsConnectionPhase,
  RTCS_RECONNECTING_DEBOUNCE_MS,
} from '@modules/ServiceModule/workspace/lib/rtcsConnectionStatus';
import { getRtcsAnnouncementCopy } from '../lib/rtcsConnectionAnnouncement';

function useDebouncedBoolean(active: boolean, delayMs: number): boolean {
  const [debounced, setDebounced] = useState(false);

  useEffect(() => {
    if (!active) {
      setDebounced(false);
      return;
    }
    const id = window.setTimeout(() => setDebounced(true), delayMs);
    return () => window.clearTimeout(id);
  }, [active, delayMs]);

  return debounced;
}

export function useRtcsConnectionAnnouncement({
  enabled,
  loading,
  error,
  serviceConnected,
  callsDown,
}: {
  enabled: boolean;
  loading: boolean;
  error: unknown;
  serviceConnected: boolean;
  callsDown: boolean;
}): {
  visible: boolean;
  phase: 'reconnecting' | 'lost' | null;
  title: string;
  status: 'warning' | 'danger';
} {
  const phase = enabled
    ? getRtcsConnectionPhase({ loading, error, serviceConnected })
    : null;
  const reconnectingImmediate = phase === 'reconnecting' && loading;
  const showReconnecting = useDebouncedBoolean(
    phase === 'reconnecting' && !loading,
    RTCS_RECONNECTING_DEBOUNCE_MS,
  );
  let visiblePhase: 'reconnecting' | 'lost' | null = null;
  if (phase === 'lost') {
    visiblePhase = 'lost';
  } else if (reconnectingImmediate || showReconnecting) {
    visiblePhase = 'reconnecting';
  }

  if (visiblePhase == null) {
    return { visible: false, phase: null, title: '', status: 'warning' };
  }

  const copy = getRtcsAnnouncementCopy(visiblePhase, callsDown);
  return { visible: true, phase: visiblePhase, ...copy };
}
